import PayloadModel from "model/_payload/payload.schema";
import PendingPartnersModel from "model/partners/pending.schema";
import ApprovedPartnersModel from "model/partners/approved.schema";
import SuspendedPartnersModel from "model/partners/suspended.schema";
import RejectedPartnersModel from "model/partners/rejected.schema";
import PartnerInfoModel from "model/partners/partner.schema";
import { createBaseApi } from "../baseApi";

export const partnersApi = createBaseApi("partners", "/partners").injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    // Get Stats
    getStats: builder.query({
      query: () => ({
        url: "/stats",
        method: "GET",
      }),
      providesTags: ['StatsPartners'],
      transformResponse: (response) => {
        return response.data;
      },
    }),

    // Get Partner Info by ID
    getPartner: builder.query({
      query: (id) => {
        return {
          url: `/${id}`,
          method: "GET",
        };
      },
      providesTags: ['Partner'],
      transformResponse: (response) => {
        return PartnerInfoModel.jsonToModel(response);
      },
    }),

    // Pending Partners
    getPendingPartners: builder.query({
      query: (value) => {
        const payload = PayloadModel.modelToJson(value);
        return {
          url: "/pending",
          method: "GET",
          params: payload,
        };
      },
      providesTags: ["PendingPartners"],
      transformResponse: (response) => {
        return PendingPartnersModel.jsonToModel(response);
      },
    }),

    // Export Pending Partners
    exportPendingPartners: builder.query({
      query: (value) => {
        const payload = PayloadModel.modelToJson(value);
        return {
          url: "exports/pending",
          method: "GET",
          params: payload,
          responseHandler: (response) => response.blob(),
        };
      },
      providesTags: ["PendingPartners"],
      transformResponse: (response) => {
        return response;
      },
    }),

    // Approved Partners
    getApprovedPartners: builder.query({
      query: (value) => {
        const payload = PayloadModel.modelToJson(value);
        return {
          url: "/approved",
          method: "GET",
          params: payload,
        };
      },
      providesTags: ["ApprovedPartners"],
      transformResponse: (response) => {
        return ApprovedPartnersModel.jsonToModel(response);
      },
    }),

    // Export Approved Partners
    exportApprovedPartners: builder.query({
      query: (value) => {
        const payload = PayloadModel.modelToJson(value);
        return {
          url: "/exports/approved",
          method: "GET",
          params: payload,
          responseHandler: (response) => response.blob(),
        };
      },
      providesTags: ["ApprovedPartners"],
      transformResponse: (response) => {
        return response;
      },
    }),

    // Suspended Partners
    getSuspendedPartners: builder.query({
      query: (value) => {
        const payload = PayloadModel.modelToJson(value);
        return {
          url: "/suspended",
          method: "GET",
          params: payload,
        };
      },
      providesTags: ["SuspendedPartners"],
      transformResponse: (response) => {
        return SuspendedPartnersModel.jsonToModel(response);
      },
    }),

    // Export Suspended Partners
    exportSuspendedPartners: builder.query({
      query: (value) => {
        const payload = PayloadModel.modelToJson(value);
        return {
          url: "/exports/suspended",
          method: "GET",
          params: payload,
          responseHandler: (response) => response.blob(),
        };
      },
      providesTags: ["SuspendedPartners"],
      transformResponse: (response) => {
        return response;
      },
    }),

    // Rejected Partners
    getRejectedPartners: builder.query({
      query: (value) => {
        const payload = PayloadModel.modelToJson(value);
        return {
          url: "/rejected",
          method: "GET",
          params: payload,
        };
      },
      providesTags: ["RejectedPartners"],
      transformResponse: (response) => {
        return RejectedPartnersModel.jsonToModel(response);
      },
    }),

    // Export Rejected Partners
    exportRejectedPartners: builder.query({
      query: (value) => {
        const payload = PayloadModel.modelToJson(value);
        return {
          url: "/exports/rejected",
          method: "GET",
          params: payload,
          responseHandler: (response) => response.blob(),
        };
      },
      providesTags: ["RejectedPartners"],
      transformResponse: (response) => {
        return response;
      },
    }),

    // Update Partner Status
    updatePartnerStatus: builder.mutation({
      query: ({ ids, status, source }) => ({
        url: "/status",
        method: "PATCH",
        body: {
          ids: Array.isArray(ids) ? ids : [ids],
          status,
        },
      }),
      invalidatesTags: (result, error, { status, source }) => {
        const tags = ['Partner', 'StatsPartners'];
        // Update tags based on source
        if (["Pending", "Approved", "Suspended", "Rejected"].includes(source)) {
          tags.push(`${source}Partners`);
        }

        // Update tags based on status
        if (['Pending', 'Suspended', 'Approved', 'Rejected'].includes(status)) {
          tags.push(`${status}Partners`);
        }
        return tags;
      },
    }),
  }),
});

export const {
  useGetStatsQuery,
  useGetPartnerQuery,

  useGetPendingPartnersQuery,
  useGetApprovedPartnersQuery,
  useGetSuspendedPartnersQuery,
  useGetRejectedPartnersQuery,

  useLazyExportPendingPartnersQuery,
  useLazyExportApprovedPartnersQuery,
  useLazyExportSuspendedPartnersQuery,
  useLazyExportRejectedPartnersQuery,

  useUpdatePartnerStatusMutation,
} = partnersApi;
